/**
 * UUIDv4 for the `Idempotency-Key` the sale endpoints require.
 *
 * `crypto.randomUUID()` exists only in a SECURE CONTEXT — HTTPS or localhost. The till
 * is served over plain HTTP on the staging ALB (`http://<alb>:3104`), so the property is
 * undefined there and calling it threw
 *
 *   TypeError: crypto.randomUUID is not a function
 *
 * from React's render path, which React reports as "Unexpected Application Error!" —
 * the whole checkout screen was replaced by an error page at the moment the operator
 * pressed Bayar, with no sale recorded. It worked in local dev because localhost counts
 * as a secure context.
 *
 * `crypto.getRandomValues()` has no such restriction, so the fallback is still a
 * cryptographically random v4. Same helper apps/handheld already ships
 * (src/utils/uuid.ts).
 */
export function uuidv4(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();

  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    // RFC 4122 §4.4 — version + variant bits
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return [
      hex.slice(0, 4).join(''),
      hex.slice(4, 6).join(''),
      hex.slice(6, 8).join(''),
      hex.slice(8, 10).join(''),
      hex.slice(10, 16).join(''),
    ].join('-');
  }

  // Last resort — only when neither crypto API is present at all.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
